import React, { useState, useMemo } from 'react'
import { gql, useQuery } from '@apollo/client'
import Box from '@material-ui/core/Box'
import Card from '@material-ui/core/Card'
import Grid from '@material-ui/core/Grid'
import LinearProgress from '@material-ui/core/LinearProgress'
import FormControl from '@material-ui/core/FormControl'
import TextField from '@material-ui/core/TextField'
import MenuItem from '@material-ui/core/MenuItem'
import Menu from '@material-ui/core/Menu'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import { makeStyles } from '@material-ui/core/styles'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import AddTimeMenuItems from './add-time-menu-items'
import { DateTime } from 'luxon'
import { isEmpty } from 'lodash'
import RouterLinkButton from 'components/router-link-button'
import useIsMobile from 'lib/hooks/use-is-mobile'
import {
  CALLOUT_FOREGROUND,
  OUTLINED_INPUT_BORDER,
  getOutlinedSelectMenuPaperStyles,
  getOutlinedSelectWrapperStyles,
  getOutlinedTextFieldWrapperStyles,
} from 'lib/form-field-styles'
import useCalendarStyles from 'pages/pga-coach/bookings/schedule/calendar/calendar-styles'
import { GET_COACH_AVAILABILITY_OVERRIDES } from '../custom-availability/queries'
import OneTimeSlotDialog from './one-time-slot-dialog'
import AddTimeMenu from './add-time-menu'
import AvailabilityCalendarGrid from './availability-calendar-grid'
import RecurringHoursSidebar from './recurring-hours-sidebar'
import WeekNavigator from './week-navigator'
import Legend from './legend'
import OverridesList from './overrides-list'
import BlocksList from './blocks-list'
import GoogleCalendarIntegration from '../../blocked-times/block-time-manager/google-calendar-integration'
import { getWeekRange, formatFacilityLabel } from './utils'

const useTabStyles = makeStyles(theme => ({
  facilityField: {
    ...getOutlinedSelectWrapperStyles(theme),
    ...getOutlinedTextFieldWrapperStyles(theme),
    flex: '0 1 auto',
    minWidth: 0,
    maxWidth: 320,
    margin: 0,
    width: 'auto',
    '& .MuiFormControl-root': {
      width: 'auto',
    },
    '& .MuiInputLabel-outlined': {
      display: 'none',
    },
    '& .MuiOutlinedInput-notchedOutline legend': {
      display: 'none',
    },
    [theme.breakpoints.only('xs')]: {
      maxWidth: '100%',
      flex: '1 1 100%',
    },
  },
  facilityMenuPaper: getOutlinedSelectMenuPaperStyles(theme),
  sectionCard: {
    padding: theme.spacing(3),
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    boxShadow: 'none',
    backgroundColor: theme.palette.common.white,
    [theme.breakpoints.only('xs')]: {
      padding: theme.spacing(2),
    },
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.only('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  sectionTitle: {
    fontWeight: 700,
    lineHeight: 1.3,
    color: CALLOUT_FOREGROUND,
  },
  sectionDescription: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  sectionAction: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
    alignSelf: 'flex-start',
  },
  callout: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#EBF0F2',
    border: `1px solid ${OUTLINED_INPUT_BORDER}`,
    borderRadius: 12,
    color: CALLOUT_FOREGROUND,
    marginBottom: theme.spacing(2),
  },
  calloutIcon: {
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  calendarSyncBox: {
    marginBottom: theme.spacing(2),
    '& > *': {
      border: '1px solid #E0E0E0 !important',
      borderRadius: 12,
      backgroundColor: theme.palette.common.white,
    },
    '& .MuiButton-outlined': {
      height: 40,
      borderRadius: 8,
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '0.875rem',
      boxShadow: 'none',
      borderColor: `${OUTLINED_INPUT_BORDER} !important`,
      color: `${CALLOUT_FOREGROUND} !important`,
      backgroundColor: theme.palette.common.white,
      '&:hover': {
        backgroundColor: '#F4F6F8',
        borderColor: `${OUTLINED_INPUT_BORDER} !important`,
      },
    },
  },
}))

const GET_AVAILABILITY = gql`
  query CoachAvailability {
    coachAvailabilities {
      coachFacility {
        id
        name
      }
      startDate
      timeZone
      timeSlots {
        day
        startTime
        endTime
      }
    }
  }
`

const GET_BLOCK_TIMES_FOR_WEEK = gql`
  query CoachAvailabilityBlockTimesForWeek($datetimeRange: DateTimeRangeInput) {
    coachAvailabilityBlockTimes(datetimeRange: $datetimeRange) {
      id
      startTime
      endTime
      allDay
      blockNote
      coachFacility {
        id
        name
      }
    }
  }
`

const SectionHeader = ({ title, description, action, classes }) => (
  <Box className={classes.sectionHeader}>
    <Box>
      <Typography variant="h6" className={classes.sectionTitle}>
        {title}
      </Typography>
      <Typography variant="body2" className={classes.sectionDescription}>
        {description}
      </Typography>
    </Box>
    {action && <Box className={classes.sectionAction}>{action}</Box>}
  </Box>
)

const AvailabilityTab = ({
  refetchCoachOnboardingStatus,
  HeaderActionsPortal,
}) => {
  const tabClasses = useTabStyles()
  const calendarClasses = useCalendarStyles()
  const isMobile = useIsMobile()
  const [selectedFacilityId, setSelectedFacilityId] = useState(null)
  const [referenceDate, setReferenceDate] = useState(() => DateTime.local())
  const weekRange = useMemo(() => getWeekRange(referenceDate), [referenceDate])
  const [createMenu, setCreateMenu] = useState(null)
  const [createDialog, setCreateDialog] = useState(null)
  const viewingThisWeek = useMemo(() => {
    const today = DateTime.local().startOf('day')
    return (
      today >= weekRange.start.startOf('day') &&
      today <= weekRange.end.endOf('day')
    )
  }, [weekRange])

  const {
    data: availabilityData,
    loading: availabilityLoading,
    refetch: refetchAvailability,
  } = useQuery(GET_AVAILABILITY)

  const {
    data: overridesData,
    loading: overridesLoading,
    refetch: refetchOverrides,
  } = useQuery(GET_COACH_AVAILABILITY_OVERRIDES, {
    variables: {
      dateRange: { from: DateTime.local().toISODate() },
    },
  })

  const { data: blocksData, refetch: refetchBlocks } = useQuery(
    GET_BLOCK_TIMES_FOR_WEEK,
    {
      variables: {
        datetimeRange: {
          from: weekRange.start.startOf('day').toISO(),
          to: weekRange.end.endOf('day').toISO(),
        },
      },
    },
  )

  const loading = availabilityLoading || overridesLoading

  if (loading) {
    return (
      <Box p={2}>
        <LinearProgress />
      </Box>
    )
  }

  if (isEmpty(availabilityData?.coachAvailabilities)) {
    return (
      <Box>
        <Typography variant="h6" color="textPrimary">
          You have not set up any facilities.
        </Typography>
        <Box mt={2}>
          <RouterLinkButton
            variant="contained"
            color="primary"
            to="/pga-coach/facilities/new"
          >
            Add Facility
          </RouterLinkButton>
        </Box>
      </Box>
    )
  }

  const availabilities = availabilityData.coachAvailabilities
  const selectedAvailability = selectedFacilityId
    ? availabilities.find(a => a.coachFacility.id === selectedFacilityId)
    : availabilities[0]

  const facilities = Array.from(
    new Map(
      availabilities.map(a => [a.coachFacility.id, a.coachFacility]),
    ).values(),
  )

  const allOverrides = overridesData?.coachAvailabilityOverrides || []
  const overridesForFacility = allOverrides.filter(
    o => o.coachFacility?.id === selectedAvailability.coachFacility.id,
  )

  const overridesForWeek = overridesForFacility.filter(o => {
    const d = DateTime.fromISO(o.date)
    return (
      d >= weekRange.start.startOf('day') && d <= weekRange.end.endOf('day')
    )
  })

  const blocksForWeek = (blocksData?.coachAvailabilityBlockTimes || []).filter(
    block => {
      const facilityId = block.coachFacility?.id
      if (!facilityId) return true
      return facilityId === selectedAvailability.coachFacility.id
    },
  )

  const handleSuccess = () => {
    refetchAvailability()
    refetchOverrides()
    refetchBlocks()
    refetchCoachOnboardingStatus && refetchCoachOnboardingStatus()
  }

  const openCreateDialog = type => {
    if (!createMenu) return
    setCreateDialog({
      initialType: type,
      defaultDate: createMenu.date,
      defaultStartTime: createMenu.startTime,
    })
    setCreateMenu(null)
  }

  return (
    <Box>
      {HeaderActionsPortal && (
        <HeaderActionsPortal>
          <AddTimeMenu
            facilities={facilities}
            onSuccess={handleSuccess}
            compact={isMobile}
            defaultFacilityId={selectedAvailability.coachFacility.id}
            existingOverrides={allOverrides}
          />
        </HeaderActionsPortal>
      )}

      <Menu
        anchorReference="anchorPosition"
        anchorPosition={
          createMenu
            ? {
              left: createMenu.anchorPosition.left,
              top: createMenu.anchorPosition.top,
            }
            : undefined
        }
        open={Boolean(createMenu)}
        onClose={() => setCreateMenu(null)}
      >
        <AddTimeMenuItems
          onSelectOpen={() => openCreateDialog('open')}
          onSelectBlocked={() => openCreateDialog('block')}
        />
      </Menu>

      {createDialog && (
        <OneTimeSlotDialog
          facilities={facilities}
          onSuccess={() => {
            handleSuccess()
            setCreateDialog(null)
          }}
          triggerless
          isOpen
          onClose={() => setCreateDialog(null)}
          initialType={createDialog.initialType}
          defaultFacilityId={selectedAvailability.coachFacility.id}
          defaultDate={createDialog.defaultDate}
          defaultStartTime={createDialog.defaultStartTime}
          existingOverrides={
            createDialog.initialType === 'open' ? allOverrides : undefined
          }
        />
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card className={calendarClasses.calendarCard} elevation={0}>
            <Box className={calendarClasses.controlBar}>
              <Box className={calendarClasses.toolbarFilters}>
                <Button
                  variant="outlined"
                  className={`${calendarClasses.todayButton} ${
                    viewingThisWeek ? calendarClasses.todayButtonSelected : ''
                  }`}
                  onClick={() => setReferenceDate(DateTime.local())}
                  aria-pressed={viewingThisWeek}
                >
                  Today
                </Button>
                <WeekNavigator
                  weekRange={weekRange}
                  referenceDate={referenceDate}
                  onJumpToDate={picked => setReferenceDate(picked)}
                />
                <FormControl
                  variant="outlined"
                  size="small"
                  className={tabClasses.facilityField}
                >
                  {availabilities.length === 1 ? (
                    <TextField
                      disabled
                      size="small"
                      value={formatFacilityLabel(selectedAvailability)}
                      variant="outlined"
                      InputLabelProps={{ shrink: false }}
                      inputProps={{ 'aria-label': 'Facility' }}
                    />
                  ) : (
                    <TextField
                      select
                      size="small"
                      value={selectedAvailability.coachFacility.id}
                      variant="outlined"
                      onChange={e => setSelectedFacilityId(e.target.value)}
                      InputLabelProps={{ shrink: false }}
                      inputProps={{ 'aria-label': 'Facility' }}
                      SelectProps={{
                        MenuProps: {
                          classes: { paper: tabClasses.facilityMenuPaper },
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                          getContentAnchorEl: null,
                        },
                      }}
                    >
                      {availabilities.map(a => (
                        <MenuItem
                          key={a.coachFacility.id}
                          value={a.coachFacility.id}
                        >
                          {formatFacilityLabel(a)}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </FormControl>
              </Box>
            </Box>
            <AvailabilityCalendarGrid
              weekRange={weekRange}
              recurringTimeSlots={selectedAvailability.timeSlots}
              overrides={overridesForWeek}
              blocks={blocksForWeek}
              timeZone={selectedAvailability.timeZone}
              onCellClick={info => setCreateMenu(info)}
            />
            <Legend />
          </Card>
        </Grid>

        <Grid item xs={12}>
          <RecurringHoursSidebar
            availability={selectedAvailability}
            onSuccess={handleSuccess}
          />
        </Grid>

        <Grid item xs={12}>
          <Card className={tabClasses.sectionCard} elevation={0}>
            <SectionHeader
              classes={tabClasses}
              title="Open Time"
              description="Bookable. One-time openings outside your recurring hours — perfect for adding extra availability on a specific day."
              action={
                overridesForFacility.length > 0 ? (
                  <OneTimeSlotDialog
                    facilities={facilities}
                    onSuccess={handleSuccess}
                    initialType="open"
                    defaultFacilityId={selectedAvailability.coachFacility.id}
                    existingOverrides={allOverrides}
                  />
                ) : null
              }
            />
            <OverridesList
              overrides={overridesForFacility}
              onChanged={refetchOverrides}
              emptyAction={
                <OneTimeSlotDialog
                  tonal
                  facilities={facilities}
                  onSuccess={handleSuccess}
                  initialType="open"
                  defaultFacilityId={selectedAvailability.coachFacility.id}
                  existingOverrides={allOverrides}
                />
              }
            />
          </Card>
        </Grid>

        <Grid item xs={12} id="blocked-time">
          <Card className={tabClasses.sectionCard} elevation={0}>
            <SectionHeader
              classes={tabClasses}
              title="Blocked Time"
              description="Not bookable. Block off vacations, time off, or other commitments. Blocked time overrides your recurring hours and any open time."
              action={
                blocksForWeek.length > 0 ? (
                  <OneTimeSlotDialog
                    facilities={facilities}
                    onSuccess={handleSuccess}
                    initialType="block"
                    defaultFacilityId={selectedAvailability.coachFacility.id}
                  />
                ) : null
              }
            />
            <Box className={tabClasses.callout}>
              <InfoOutlinedIcon
                fontSize="small"
                className={tabClasses.calloutIcon}
              />
              <Typography variant="body2">
                Students won't be able to book sessions with you during a
                blocked time.
              </Typography>
            </Box>
            <Box className={tabClasses.calendarSyncBox}>
              <GoogleCalendarIntegration onIntegrationChange={refetchBlocks} />
            </Box>
            <BlocksList
              blocks={blocksForWeek}
              onChanged={refetchBlocks}
              emptyAction={
                <OneTimeSlotDialog
                  tonal
                  facilities={facilities}
                  onSuccess={handleSuccess}
                  initialType="block"
                  defaultFacilityId={selectedAvailability.coachFacility.id}
                />
              }
            />
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default AvailabilityTab
